import { useEffect, useRef } from "react";
import { Backdrop } from "../components/Backdrop";
import { CLIENT_ID } from "../config";
import type { HandshakeDiagnostics } from "../discord/handshake";
import { useT } from "../i18n/context";
import { shortId } from "../ops/report";
import { useStore } from "../store";

/** Shown only when Discord never answered the handshake: which side is silent. */
function Diagnostics({ diag }: { diag: HandshakeDiagnostics }) {
  const lines = [
    `frame ${diag.frameId ?? "-"} | ${diag.platform ?? "-"} | ${diag.nested ? "nested" : "top"}`,
    `ref ${diag.referrer || "-"}`,
    `in ${diag.received.join(", ") || "nothing"}`,
    `client ${CLIENT_ID.slice(0, 6)}…`,
    `why ${diag.reason ?? "-"}`,
  ];
  return (
    <pre className="relative max-w-full overflow-x-auto p-2 text-start text-[11px] text-dim">
      {lines.join("\n")}
    </pre>
  );
}

/**
 * First screen (design 1, 1b): the logo, "connecting" while Discord and the server
 * answer, a red line and "Try again" when they do not. See docs/ekranlar/yetkilendirme.md.
 */
export function Authorize({ onRetry }: { onRetry: () => void }) {
  const { t } = useT();
  const phase = useStore((s) => s.phase);
  const error = useStore((s) => s.error);
  const diagnostics = useStore((s) => s.diagnostics);
  const eventId = useStore((s) => s.eventId);
  const failed = phase === "failed" && error !== null;
  const retry = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (failed) retry.current?.focus();
  }, [failed]);
  return (
    <div className="relative isolate flex h-full flex-col items-center justify-center gap-6 overflow-hidden p-4 pb-20 font-ui text-ink">
      <Backdrop />
      <img
        src="/logo.svg"
        alt={t("app.name")}
        className="relative w-[min(80vw,460px)] drop-shadow-[0_16px_30px_rgb(0_0_0/0.7)]"
      />
      {failed ? (
        <div className="relative flex flex-col items-center gap-3.5">
          <p
            aria-live="polite"
            className="max-w-[min(92vw,560px)] rounded-[28px] border-[1.5px] border-danger-soft/70 bg-danger/40 px-[26px] py-3.5 text-center text-[18px] font-bold shadow-panel"
          >
            {t(`auth.${error}`)}
          </p>
          {eventId !== null && (
            <p className="text-[12px] text-dim">{t("error.eventId", { id: shortId(eventId) })}</p>
          )}
          <button
            ref={retry}
            type="button"
            className="btn-gold h-14 rounded-pill px-[34px] text-[17px] font-extrabold"
            onClick={onRetry}
          >
            {t("auth.retry")}
          </button>
        </div>
      ) : (
        <p
          aria-live="polite"
          className="relative flex items-center gap-4 rounded-pill border-[1.5px] border-edge bg-panel px-[26px] py-3.5 text-[20px] font-bold shadow-panel"
        >
          <span
            aria-hidden="true"
            className="h-[26px] w-[26px] flex-none animate-spin rounded-full border-[3px] border-ink/25 border-t-gold"
          />
          {t("auth.connecting")}
        </p>
      )}
      {failed && diagnostics !== null ? <Diagnostics diag={diagnostics} /> : null}
    </div>
  );
}
